import React, { useEffect, useState } from 'react';
import { getPlans } from '../core/subscriptionService';
import Select from 'react-select';

export const CheckoutConfigTab: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPlans().then((data) => {
      setPlans(data);
      if (data.length > 0) setSelectedPlan(data[0]);
      setLoading(false);
    }).catch(() => {
      setPlans([]);
      setLoading(false);
    });
  }, []);

  const planItems = selectedPlan?.planItems || [];
  const adhesionFee = Number(selectedPlan?.adhesion_fee) || 0;

  const itemsTotal = planItems.reduce((sum: number, item: any) => {
    return sum + (Number(item.unit_price) || 0) * (Number(item.default_quantity) || 1);
  }, 0);

  const totalEstimated = adhesionFee + itemsTotal;

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-8'>
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bolder fs-3 mb-1'>Configuração do Checkout</span>
              <span className='text-muted mt-1 fw-bold fs-7'>Visualize como os planos e seus itens serão calculados para o cliente.</span>
            </h3>
            <div className='card-toolbar'>
              {plans.length > 0 && (
                <Select
                  options={plans.map((p) => ({ value: p.id, label: p.name, plan: p }))}
                  value={selectedPlan ? { value: selectedPlan.id, label: selectedPlan.name, plan: selectedPlan } : null}
                  onChange={(val: any) => setSelectedPlan(val?.plan || null)}
                  className='react-select-container w-250px'
                  classNamePrefix='react-select'
                  isSearchable
                  placeholder='Selecione um plano...'
                />
              )}
            </div>
          </div>
          <div className='card-body py-3'>
            {loading ? (
              <div className='d-flex justify-content-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : !selectedPlan ? (
              <div className='d-flex flex-column align-items-center justify-content-center py-15'>
                <i className='bi bi-box-seam fs-2tx text-gray-300 mb-5'></i>
                <span className='text-gray-500 fw-bold fs-5'>Nenhum plano encontrado.</span>
                <span className='text-muted fs-7 mt-2'>Crie um plano na aba "Planos" para visualizar a configuração do checkout.</span>
              </div>
            ) : (
              <div className='d-flex flex-column gap-5'>
                <div className='border border-dashed border-gray-300 rounded p-7'>
                  <h4>Informações do Plano</h4>
                  <div className='d-flex justify-content-between align-items-center mt-5 p-4 bg-light rounded'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-40px me-4'>
                        <span className='symbol-label bg-light-primary'>
                          <i className='bi bi-award text-primary fs-3'></i>
                        </span>
                      </div>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fw-bolder fs-5'>{selectedPlan.name}</span>
                        <span className='text-muted fw-bold'>{selectedPlan.periodicity || 'MENSAL'} • Status: {selectedPlan.status || 'ATIVO'}</span>
                      </div>
                    </div>
                    <div className='d-flex flex-column align-items-end'>
                      <span className='text-muted fw-bold fs-7'>Taxa de Adesão</span>
                      <span className='text-gray-800 fw-bolder fs-5'>R$ {adhesionFee.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                <div className='border border-dashed border-gray-300 rounded p-7'>
                  <h4>Itens do Plano</h4>
                  {planItems.length === 0 ? (
                    <div className='d-flex flex-column align-items-center py-8'>
                      <i className='bi bi-list-check fs-2tx text-gray-300 mb-3'></i>
                      <span className='text-muted fw-bold'>Este plano não possui itens configurados.</span>
                    </div>
                  ) : (
                    <div className='d-flex flex-column gap-3 mt-4'>
                      {planItems.map((item: any, idx: number) => (
                        <div key={item.id || idx} className='d-flex justify-content-between align-items-center p-4 bg-light rounded'>
                          <div className='d-flex align-items-center'>
                            <div className='symbol symbol-35px me-3'>
                              <span className={`symbol-label bg-light-${item.item_type === 'SERVICO' ? 'warning' : 'info'}`}>
                                <i className={`bi ${item.item_type === 'SERVICO' ? 'bi-tools' : 'bi-box'} text-${item.item_type === 'SERVICO' ? 'warning' : 'info'} fs-5`}></i>
                              </span>
                            </div>
                            <div className='d-flex flex-column'>
                              <span className='text-gray-800 fw-bolder fs-6'>
                                {item.product?.name || item.product?.description || item.typeService?.description || item.typeService?.name || `Item ${idx + 1}`}
                              </span>
                              <span className='text-muted fw-bold fs-7'>
                                {item.quantity_mode === 'FIXED' ? 'Quantidade fixa' :
                                 item.quantity_mode === 'PER_PANEL' ? 'Por painel' :
                                 item.quantity_mode === 'PER_KWP' ? 'Por kWp' :
                                 item.quantity_mode === 'PER_POWER' ? 'Por potência' : 'Fixo'}
                                {item.is_required ? ' • Obrigatório' : ' • Opcional'}
                                {` • Qtd. padrão: ${Number(item.default_quantity) || 1}`}
                              </span>
                            </div>
                          </div>
                          <div className='d-flex flex-column align-items-end'>
                            <span className='text-muted fw-bold fs-8'>Subtotal</span>
                            <span className='text-gray-800 fw-bolder text-end'>
                              {Number(item.default_quantity) || 1} × R$ {(Number(item.unit_price) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br />
                              <span className='fs-5 text-primary'>= R$ {((Number(item.unit_price) || 0) * (Number(item.default_quantity) || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='col-xl-4'>
        <div className='card border border-dashed border-gray-300 mb-5 mb-xl-8'>
          <div className='card-body p-8'>
            <h3 className='text-gray-800 mb-1'>Preview do Cálculo</h3>
            <span className='text-gray-500 fw-bold fs-6'>
              {selectedPlan ? `Baseado no plano "${selectedPlan.name}"` : 'Selecione um plano'}
            </span>

            <div className='d-flex justify-content-between align-items-center mt-8 mb-4'>
              <span className='text-gray-600 fw-bold'>Taxa de Adesão</span>
              <span className='text-gray-800 fw-bolder'>R$ {adhesionFee.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            {planItems.length > 0 && (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <span className='text-gray-600 fw-bold'>Itens ({planItems.length})</span>
                  <span className='text-gray-800 fw-bolder'>R$ {itemsTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className='d-flex flex-column gap-2 mb-4 ps-4'>
                  {planItems.map((item: any, idx: number) => {
                    const qty = Number(item.default_quantity) || 1;
                    const price = Number(item.unit_price) || 0;
                    const sub = qty * price;
                    return (
                      <div key={idx} className='d-flex justify-content-between align-items-center'>
                        <span className='text-gray-500 fs-7'>↳ {item.product?.name || item.product?.description || item.typeService?.description || item.typeService?.name || `Item ${idx + 1}`} ({qty} × R$ {price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})</span>
                        <span className='text-gray-400 fs-7'>R$ {sub.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className='separator separator-dashed border-gray-300 mb-6'></div>

            <div className='d-flex justify-content-between align-items-center bg-light-primary rounded p-4'>
              <span className='text-primary fw-bolder fs-4'>Total Estimado</span>
              <span className='text-primary fw-bolder fs-2'>R$ {totalEstimated.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <div className='card mb-5 border border-dashed border-primary'>
          <div className='card-body p-6'>
            <div className='d-flex align-items-center mb-3'>
              <i className='bi bi-lightbulb fs-2x text-primary me-3'></i>
              <span className='fw-bolder fs-5'>Dica Técnica</span>
            </div>
            <span className='text-muted fw-bold'>
              Os itens do checkout são definidos diretamente na configuração de cada plano (aba "Planos"). Cada item pode ter um modo de cálculo: fixo, por painel, por kWp ou por potência.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
